import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { updateFetcherData } from "@prismicio/editor-support/Suspense"
import {
  Box,
  EditableText,
  Icon,
  useAlert,
  useToast
} from "@prismicio/editor-ui"
import { useState } from "react"
import { z } from "zod"

import { staticEditorConfig } from "../../app/editorConfig"
import { getAllReleases } from "../../app/services/release"

interface ReleaseNameInputProps {
  defaultValue?: string
  isEditable?: boolean
  releaseId?: string
}

export function ReleaseNameInput(props: ReleaseNameInputProps) {
  const { defaultValue, isEditable = true, releaseId } = props
  const [value, setValue] = useState(defaultValue || "")
  const [loading, setLoading] = useState(false)

  const { showAlert } = useAlert()
  const { showToast } = useToast()

  function onBlur() {
    if (!releaseId) return
    if (value === defaultValue) return

    setLoading(true)
    updateReleaseName({ releaseId, name: value })
      .then(() => {
        setLoading(false)
        updateFetcherData(getAllReleases, (body) => {
          // update cached release name
          return body.map((release) =>
            release.id === releaseId ? { ...release, label: value } : release
          )
        })
        showToast({
          title: "Release has been successfully renamed"
        })
      })
      .catch(() => {
        setLoading(false)
        setValue(defaultValue || "")
        showAlert({
          title: "Error",
          subtitle: `Something went wrong when renaming ${
            defaultValue ?? "..."
          }.`
        })
      })
  }

  return (
    <Box gap={4} alignItems="center">
      <EditableText
        aria-label="Edit Release Name"
        value={value}
        variant="h3"
        color="grey12"
        onBlur={onBlur}
        onChange={setValue}
        disabled={!isEditable || loading}
      />
      {isEditable && <Icon name="edit" size="medium" color="grey11" />}
    </Box>
  )
}

interface updateReleaseNameArgs {
  releaseId: string
  name: string
}

function updateReleaseName(args: updateReleaseNameArgs) {
  const { releaseId, name } = args

  const config = staticEditorConfig

  if (!config.mediaLibrary) throw new Error("Media Library not configured.")

  const { authStrategy, coreApiBaseUrl } = config

  return request(new URL(`releases/${releaseId}/rename`, coreApiBaseUrl), {
    body: {
      name
    },
    method: "POST",
    credentials: getRequestCredentials(authStrategy),
    okType: z.unknown()
  })
}
